import React, { useState, useEffect, useId, useRef, useCallback } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { 
  Mail, 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  Eye, 
  Send, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  TrendingUp,
  Users,
  MousePointer,
  MailOpen,
  Activity
} from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { canViewEmailStats } from '../../utils/emailManagementAccess';
import { 
  emailService, 
  EmailNotificationHistoryDto, 
  EmailNotificationStatsDto, 
  PaginationParams,
  EmailDeliveryStatus,
  deliveryStatusLabel,
} from '../../services/emailService';

interface FilterState {
  search: string;
  status: string;
  priority: string;
  dateFrom: string;
  dateTo: string;
  templateName: string;
}

export const EmailNotificationHistory: React.FC = () => {
  const { user } = useAuth();

  // The statistics endpoint is Admin,Manager while this history is Admin,Agent, so an
  // Agent can read the list but not the aggregates (BugHerd #158).
  const statsAvailable = canViewEmailStats(user?.role);
  const [notifications, setNotifications] = useState<EmailNotificationHistoryDto[]>([]);
  const [stats, setStats] = useState<EmailNotificationStatsDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    status: '',
    priority: '',
    dateFrom: '',
    dateTo: '',
    templateName: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<EmailNotificationHistoryDto | null>(null);

  // BugHerd #132: give the detail overlay real dialog semantics and focus
  // management. Only the dialog chrome is touched here - the detail fields
  // themselves are tracked separately under #163.
  const detailDialogRef = useRef<HTMLDivElement>(null);
  const detailTitleId = useId();
  const closeDetailModal = useCallback(() => setShowDetailModal(false), []);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useModalA11y({
    isOpen: showDetailModal && !!selectedNotification,
    onClose: closeDetailModal,
    containerRef: detailDialogRef,
  });

  useEffect(() => {
    loadData();
  }, [pagination.page, filters]);

  const loadData = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const params: PaginationParams = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search || undefined,
        filters: {
          status: filters.status || undefined,
          priority: filters.priority || undefined,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          templateName: filters.templateName || undefined
        }
      };

      const [historyResponse, statsData] = await Promise.all([
        emailService.getNotificationHistory(params),
        statsAvailable ? emailService.getNotificationStats().catch(() => null) : Promise.resolve(null)
      ]);

      const items: EmailNotificationHistoryDto[] = Array.isArray(historyResponse)
        ? historyResponse
        : (historyResponse as any)?.items || (historyResponse as any)?.data?.items || (historyResponse as any)?.data || [];

      setNotifications(items);
      setPagination({
        page: (historyResponse as any)?.pageNumber || (historyResponse as any)?.data?.pageNumber || 1,
        pageSize: (historyResponse as any)?.pageSize || (historyResponse as any)?.data?.pageSize || 20,
        totalCount: (historyResponse as any)?.totalCount || (historyResponse as any)?.data?.totalCount || items.length,
        totalPages: (historyResponse as any)?.totalPages || (historyResponse as any)?.data?.totalPages || 1,
        hasNextPage: !!((historyResponse as any)?.hasNextPage || (historyResponse as any)?.data?.hasNextPage),
        hasPreviousPage: !!((historyResponse as any)?.hasPreviousPage || (historyResponse as any)?.data?.hasPreviousPage)
      });
      setStats((statsData as any)?.data || statsData);
    } catch (error: any) {
      setLoadError('Unable to load email notification history. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  };

  const handleFilterChange = (key: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      priority: '',
      dateFrom: '',
      dateTo: '',
      templateName: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewDetails = (notification: EmailNotificationHistoryDto) => {
    setSelectedNotification(notification);
    setShowDetailModal(true);
  };

  const handleExport = async () => {
    try {
      // This would typically trigger a download of CSV/Excel file
      // For now, we'll generate a simple CSV export
      const csvContent = generateCSVContent(notifications);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-notifications-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const generateCSVContent = (data: EmailNotificationHistoryDto[]): string => {
    const headers = ['Date', 'Recipient', 'Subject', 'Template', 'Status', 'Priority', 'Opens', 'Clicks'];
    const rows = data.map(notification => [
      new Date(notification.sentAt).toLocaleDateString(),
      notification.recipient,
      notification.subject,
      notification.templateName || 'Custom',
      deliveryStatusLabel(notification.deliveryStatus),
      notification.priority,
      notification.openCount?.toString() || '0',
      notification.clickCount?.toString() || '0'
    ]);
    
    return [headers, ...rows].map(row => row.map(field => `"${field}"`).join(',')).join('\n');
  };

  const getStatusIcon = (status: EmailDeliveryStatus | number | null | undefined) => {
    switch (status) {
      case EmailDeliveryStatus.Sent:
      case EmailDeliveryStatus.Delivered:
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case EmailDeliveryStatus.Failed:
      case EmailDeliveryStatus.Bounced:
        return <XCircle className="w-4 h-4 text-red-600" />;
      case EmailDeliveryStatus.Pending:
        return <Clock className="w-4 h-4 text-yellow-600" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: EmailDeliveryStatus | number | null | undefined): string => {
    switch (status) {
      case EmailDeliveryStatus.Sent:
      case EmailDeliveryStatus.Delivered:
        return 'success';
      case EmailDeliveryStatus.Failed:
      case EmailDeliveryStatus.Bounced:
        return 'error';
      case EmailDeliveryStatus.Pending:
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority?: string): string => {
    switch ((priority ?? '').toLowerCase()) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'default';
      case 'low':
        return 'secondary';
      default:
        return 'default';
    }
  };

  /**
   * BugHerd #163: this was called with selectedNotification.createdAt, a field the API does
   * not return. new Date(undefined) is an Invalid Date, and both toLocale* calls then return
   * the string "Invalid Date", producing the reported "Created:Invalid Date Invalid Date".
   * A genuinely absent timestamp now says so.
   */
  const formatDate = (dateString?: string | null): string => {
    if (!dateString) return 'Not recorded';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Not recorded';
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading && !isRefreshing) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Loading email notifications...</span>
        </div>
      </div>
    );
  }

  if (loadError) return <div className="space-y-4">
    <h2 className="text-2xl font-bold text-gray-900">Email Notification History</h2>
    <div role="alert" className="rounded border border-red-200 bg-red-50 p-4 text-red-800">{loadError}</div>
    <Button onClick={() => void loadData()}>Retry</Button>
  </div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Notification History</h2>
          <p className="text-gray-600 mt-1">Track and monitor all email notifications sent from the system</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleRefresh}
            disabled={isRefreshing}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
          <Button
            onClick={handleExport}
            variant="outline"
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </Button>
        </div>
      </div>

      {/* Statistics Cards. When the signed-in role cannot read them the block used to
          disappear with no explanation, because the 403 was swallowed to null - the page
          simply looked as though it had no statistics (BugHerd #158). */}
      {!statsAvailable && (
        <Card>
          <div className="p-4 text-sm text-gray-600" data-testid="stats-not-permitted">
            Delivery statistics are not available for your role. The notification history
            below is complete.
          </div>
        </Card>
      )}

      {statsAvailable && stats && (
        <>
          {/* BugHerd #161: this panel was bound to a DTO the API does not return. Of the
              eight fields it read, seven were absent - totalSent, last24Hours,
              totalDelivered, openRate, totalOpened, clickRate, totalClicked - so every card
              rendered its `?? 0` fallback. The one field that does exist, deliveryRate, is
              already a percentage (EmailController computes delivered/attempted*100) and was
              multiplied by 100 again, producing "10000.0%". The cards below read the fields
              the endpoint actually returns, and each total names its period. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Sent today</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {(stats.totalSentToday ?? 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Send className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    This week: {(stats.totalSentThisWeek ?? 0).toLocaleString()}
                    {' · '}
                    This month: {(stats.totalSentThisMonth ?? 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Delivery rate today</p>
                    <p className="text-3xl font-bold text-gray-900" data-testid="delivery-rate">
                      {(stats.deliveryRate ?? 0).toFixed(1)}%
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    Failed today: {(stats.failedToday ?? 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending delivery</p>
                    <p className="text-3xl font-bold text-gray-900">
                      {(stats.pendingDelivery ?? 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
                <div className="mt-4">
                  <span className="text-sm text-gray-600">
                    Avg delivery: {(stats.averageDeliveryTimeMinutes ?? 0).toFixed(1)} min
                  </span>
                </div>
              </div>
            </Card>
          </div>

          {/* Open and click rates were previously shown here. The API collects no engagement
              data at all - there is no openRate, clickRate, totalOpened or totalClicked
              anywhere in EmailController - so those cards could only ever have displayed
              zero. Saying so is more useful than four zeroes. */}
          <p className="text-sm text-gray-500" data-testid="engagement-not-tracked">
            Open and click tracking is not collected, so engagement rates are not shown.
          </p>
        </>
      )}

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Filter className="w-4 h-4" />
              <span>Filters</span>
            </Button>
            {Object.values(filters).some(f => f !== '') && (
              <Button onClick={clearFilters} variant="ghost" size="sm">
                Clear Filters
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="lg:col-span-2">
                <Input
                  placeholder="Search recipients, subjects..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                  <option value="bounced">Bounced</option>
                </select>
              </div>
              <div>
                <select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
              <div>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  placeholder="From Date"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  placeholder="To Date"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Notifications Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Engagement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(!notifications || notifications.length === 0) ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    <Mail className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="font-medium text-gray-700">No email notification records found</p>
                    <p className="text-xs text-gray-500 mt-1">Notifications sent by the system will appear here.</p>
                  </td>
                </tr>
              ) : (
                notifications.map((notification) => (
                  <tr key={notification.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{notification.recipient}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {notification.subject}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {notification.templateName || 'Custom'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(notification.deliveryStatus)}
                        <Badge variant={getStatusColor(notification.deliveryStatus)}>
                          {deliveryStatusLabel(notification.deliveryStatus)}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant={getPriorityColor(notification.priority)}>
                        {notification.priority}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {notification.sentAt ? formatDate(notification.sentAt) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        <div className="flex items-center space-x-1">
                          <MailOpen className="w-3 h-3" />
                          <span>{notification.openCount || 0}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <MousePointer className="w-3 h-3" />
                          <span>{notification.clickCount || 0}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        onClick={() => handleViewDetails(notification)}
                        variant="ghost"
                        size="sm"
                        className="flex items-center space-x-1"
                      >
                        <Eye className="w-3 h-3" />
                        <span>View</span>
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of{' '}
              {pagination.totalCount} results
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={!pagination.hasPreviousPage}
                variant="outline"
                size="sm"
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <Button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                variant="outline"
                size="sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      {showDetailModal && selectedNotification && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            ref={detailDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={detailTitleId}
            tabIndex={-1}
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden focus:outline-none"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 id={detailTitleId} className="text-lg font-semibold text-gray-900">Email Notification Details</h3>
              <Button
                onClick={closeDetailModal}
                variant="ghost"
                size="sm"
                className="p-1"
                aria-label="Close email notification details"
              >
                ×
              </Button>
            </div>
            
            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Recipient:</span>
                      <span className="ml-2 text-gray-900">{selectedNotification.recipient}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Subject:</span>
                      <span className="ml-2 text-gray-900">{selectedNotification.subject}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Template:</span>
                      <span className="ml-2 text-gray-900">{selectedNotification.templateName || 'Custom'}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-gray-600">Status:</span>
                      <div className="ml-2 flex items-center space-x-2">
                        {getStatusIcon(selectedNotification.deliveryStatus)}
                        <Badge variant={getStatusColor(selectedNotification.deliveryStatus)}>
                          <span data-testid="detail-status">{deliveryStatusLabel(selectedNotification.deliveryStatus)}</span>
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Priority:</span>
                      <span className="ml-2">
                        <Badge variant={getPriorityColor(selectedNotification.priority)}>
                          {selectedNotification.priority}
                        </Badge>
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Timestamps</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-600">Sent:</span>
                      <span className="ml-2 text-gray-900" data-testid="detail-sent">{formatDate(selectedNotification.sentAt)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Delivered:</span>
                      <span className="ml-2 text-gray-900" data-testid="detail-delivered">{formatDate(selectedNotification.deliveredAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* BugHerd #163: this read selectedNotification.trackingData, which the API
                  never returns, so the whole section was hidden. The endpoint supplies flat
                  openCount and clickCount fields instead. */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Tracking Information</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <MailOpen className="w-4 h-4 text-purple-600" />
                      <span className="text-gray-600">Opens:</span>
                      <span className="font-medium text-gray-900" data-testid="detail-opens">
                        {selectedNotification.openCount ?? 0}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MousePointer className="w-4 h-4 text-orange-600" />
                      <span className="text-gray-600">Clicks:</span>
                      <span className="font-medium text-gray-900" data-testid="detail-clicks">
                        {selectedNotification.clickCount ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedNotification.errorMessage && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Error Details</h4>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start space-x-2">
                      <XCircle className="w-4 h-4 text-red-600 mt-0.5" />
                      <span className="text-sm text-red-800">{selectedNotification.errorMessage}</span>
                    </div>
                  </div>
                </div>
              )}

              {selectedNotification.metadata && Object.keys(selectedNotification.metadata).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Additional Metadata</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                      {JSON.stringify(selectedNotification.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end p-6 bg-gray-50 border-t border-gray-200">
              <Button
                onClick={() => setShowDetailModal(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};